<script setup>
import { ref, computed, toRef } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import ResponsivePanel from 'dashboard/components-next/overlay/ResponsivePanel.vue';

const props = defineProps({
  activeSort: {
    type: String,
    default: 'name',
  },
  activeOrdering: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:sort']);

const { t } = useI18n();

const isMenuOpen = ref(false);

const sortMenus = [
  {
    label: t('COMPANIES.SORT_BY.OPTIONS.NAME'),
    value: 'name',
  },
  {
    label: t('COMPANIES.SORT_BY.OPTIONS.DOMAIN'),
    value: 'domain',
  },
  {
    label: t('COMPANIES.SORT_BY.OPTIONS.CREATED_AT'),
    value: 'created_at',
  },
  {
    label: t('COMPANIES.SORT_BY.OPTIONS.CONTACTS_COUNT'),
    value: 'contacts_count',
  },
];

const orderingMenus = [
  {
    label: t('COMPANIES.ORDER.OPTIONS.ASCENDING'),
    value: '',
  },
  {
    label: t('COMPANIES.ORDER.OPTIONS.DESCENDING'),
    value: '-',
  },
];

// Converted the props to refs for better reactivity
const activeSort = toRef(props, 'activeSort');

const activeOrdering = toRef(props, 'activeOrdering');

const activeSortLabel = computed(() => {
  const selectedMenu = sortMenus.find(menu => menu.value === activeSort.value);
  return selectedMenu?.label || t('COMPANIES.SORT_BY.LABEL');
});

const activeOrderingLabel = computed(() => {
  const selectedMenu = orderingMenus.find(
    menu => menu.value === activeOrdering.value
  );
  return selectedMenu?.label || t('COMPANIES.ORDER.LABEL');
});

const handleSortChange = value => {
  emit('update:sort', { sort: value, order: props.activeOrdering });
};

const handleOrderChange = value => {
  emit('update:sort', { sort: props.activeSort, order: value });
};
</script>

<template>
  <div class="relative">
    <Button
      icon="i-lucide-arrow-down-up"
      color="slate"
      size="sm"
      variant="ghost"
      :class="isMenuOpen ? 'bg-n-alpha-2' : ''"
      @click="isMenuOpen = !isMenuOpen"
    />
    <div
      v-if="isMenuOpen"
      v-on-clickaway="() => (isMenuOpen = false)"
      class="contents"
    >
      <ResponsivePanel
        :title="t('COMPANIES.SORT_BY.LABEL')"
        :close-label="t('GENERAL.CLOSE')"
        show-mobile-title
        desktop-class="md:absolute md:top-full md:mt-1 md:ltr:right-0 md:rtl:left-0"
        panel-class="md:w-72"
        content-class="flex flex-col gap-4 md:p-4"
        @close="isMenuOpen = false"
      >
        <div
          class="flex flex-col gap-2 md:flex-row md:items-center md:justify-between"
        >
          <span class="text-sm text-n-slate-12">
            {{ t('COMPANIES.SORT_BY.LABEL') }}
          </span>
          <SelectMenu
            :model-value="activeSort"
            :options="sortMenus"
            :label="activeSortLabel"
            sub-menu-position="bottom"
            @update:model-value="handleSortChange"
          />
        </div>
        <div
          class="flex flex-col gap-2 md:flex-row md:items-center md:justify-between"
        >
          <span class="text-sm text-n-slate-12">
            {{ t('COMPANIES.ORDER.LABEL') }}
          </span>
          <SelectMenu
            :model-value="activeOrdering"
            :options="orderingMenus"
            :label="activeOrderingLabel"
            sub-menu-position="bottom"
            @update:model-value="handleOrderChange"
          />
        </div>
      </ResponsivePanel>
    </div>
  </div>
</template>
